import * as React from 'react';
import { useCallback, useMemo } from 'react';
import {
    capitalize,
    ListItemText,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent
} from '@mui/material';
import { Check } from '@mui/icons-material';

import { LabelLarge, Row } from '@dh/styles';

import { EllipsisText, SingleSelectBaseSelect } from './styles';

/**
 * Same styling as MultiSelect.tsx
 * User can select one option. User cannot deselect option.
 * First option is selected by default.
 */

type SingleSelectProps = {
    /**
     * All possible selectable options
     */
    readonly allOptions: string[];
    onChange: (selectedID: string) => void;
    disabled?: boolean;
    defaultSelectedOption?: string;
    noMaxWidth?: boolean;
    leadingText?: string;
};

const SingleSelect = ({
    allOptions,
    onChange,
    disabled = false,
    noMaxWidth = false,
    leadingText,
    defaultSelectedOption = allOptions.length > 0 ? allOptions[0] : undefined
}: SingleSelectProps) => {
    const [selectedOption, setSelectedOption] = React.useState<string | undefined>(
        defaultSelectedOption
    );

    const menuItems = useMemo(
        () =>
            allOptions.map((option) => (
                <MenuItem key={option} value={option}>
                    <ListItemText primary={option} sx={{ textTransform: 'capitalize' }} />
                </MenuItem>
            )),
        [allOptions]
    );

    const handleChange = useCallback(
        (event: SelectChangeEvent<unknown>) => {
            const selectedID = event.target.value as string;
            setSelectedOption(selectedID);
            onChange(selectedID);
        },
        [onChange]
    );

    return (
        <Row>
            {leadingText && <LabelLarge mx={1}>{leadingText}</LabelLarge>}
            <SingleSelectBaseSelect
                isSelected={true}
                disabled={disabled}
                value={selectedOption ?? ''}
                noMaxWidth={noMaxWidth}
                onChange={handleChange}
                input={<OutlinedInput />}
                renderValue={(selected: unknown) => {
                    const selectedItem = allOptions.find(
                        (option) => option === (selected as string)
                    );
                    return (
                        <Row>
                            <Check sx={{ height: 18, width: 18 }} />
                            <EllipsisText mx={1}>
                                {selectedItem ? capitalize(selectedItem) : ''}
                            </EllipsisText>
                        </Row>
                    );
                }}
            >
                {menuItems}
            </SingleSelectBaseSelect>
        </Row>
    );
};

export { SingleSelect };
export type { SingleSelectProps };
